import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PackageOpen, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = {
  title: "",
  price: "",
  image_url: "",
  store: "Other",
  original_url: "",
  category_id: "none",
  is_published: true,
};

export default function ProductFormDialog({ open, onOpenChange, product, categories, onSaved }) {
  const isEdit = !!product;
  const [step, setStep] = useState("url");
  const [url, setUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [imgOk, setImgOk] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (product) {
      setForm({
        ...EMPTY,
        ...product,
        original_url: product.original_url || product.affiliate_url || "",
        category_id: product.category_id || "none",
      });
      setStep("form");
    } else {
      setForm(EMPTY);
      setUrl("");
      setStep("url");
    }
    setImgOk(true);
  }, [open, product]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleFetch = async () => {
    if (!url.trim()) {
      toast.error("Paste a product link first");
      return;
    }
    setFetching(true);
    try {
      const { data } = await api.post("/fetch-metadata", { url: url.trim() });
      setForm((f) => ({
        ...f,
        original_url: data.original_url || url.trim(),
        store: data.store || f.store,
        title: data.title || f.title,
        image_url: data.image_url || f.image_url,
        price: data.price || f.price,
      }));
      if (data.fetched) {
        toast.success("Product details fetched — review below");
      } else {
        toast.info("Couldn't fetch details automatically — fill them in below");
      }
      setStep("form");
    } catch (err) {
      toast.error(formatApiError(err));
      setForm((f) => ({ ...f, original_url: url.trim() }));
      setStep("form");
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Product title is required");
      return;
    }
    const buyUrl = form.original_url.trim() || (product?.affiliate_url ?? "");
    if (!buyUrl) {
      toast.error("Product URL is required — Buy Now sends visitors there");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      price: form.price.trim(),
      image_url: form.image_url.trim(),
      store: form.store,
      original_url: form.original_url.trim(),
      affiliate_url: buyUrl,
      is_published: form.is_published,
      ...(form.category_id === "none" ? { clear_category: true } : { category_id: form.category_id }),
    };
    try {
      if (isEdit) {
        await api.put(`/products/${product.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added to your page");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-lg" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight">{isEdit ? "Edit product" : "Add product"}</DialogTitle>
          <DialogDescription>
            {step === "url" ? "Paste an Amazon or Flipkart link and we'll fetch the details." : "Review and edit the details, then save."}
          </DialogDescription>
        </DialogHeader>

        {step === "url" && !isEdit ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="product-url">Product URL</Label>
              <Input
                id="product-url"
                data-testid="product-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleFetch()}
                placeholder="https://www.amazon.in/dp/..."
                className="rounded-xl"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleFetch}
                disabled={fetching}
                data-testid="fetch-product-button"
                className="flex-1 rounded-full bg-black py-5 text-white hover:bg-zinc-800"
              >
                {fetching ? "Fetching..." : "Fetch details"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setStep("form")}
                data-testid="manual-entry-button"
                className="rounded-full py-5"
              >
                Enter manually
              </Button>
            </div>
            <p className="text-xs leading-relaxed text-zinc-400">
              Amazon and Flipkart sometimes block automatic fetching. If that happens, you can fill in everything manually.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-black/5 bg-zinc-50">
                {form.image_url && imgOk ? (
                  <img
                    src={form.image_url}
                    alt="Preview"
                    onError={() => setImgOk(false)}
                    className="h-full w-full object-cover"
                    data-testid="product-form-image-preview"
                  />
                ) : (
                  <PackageOpen className="h-6 w-6 text-zinc-300" />
                )}
              </div>
              <div className="flex-1 space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="pf-title">Title</Label>
                  <Input id="pf-title" data-testid="product-form-title-input" value={form.title} onChange={set("title")} className="rounded-xl" placeholder="Anker Soundcore XYZ" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="pf-price">Price</Label>
                    <Input id="pf-price" data-testid="product-form-price-input" value={form.price} onChange={set("price")} className="rounded-xl" placeholder="₹4,999" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Store</Label>
                    <Select value={form.store} onValueChange={set("store")}>
                      <SelectTrigger data-testid="product-form-store-select" className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Amazon">Amazon</SelectItem>
                        <SelectItem value="Flipkart">Flipkart</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pf-image">Image URL</Label>
              <Input
                id="pf-image"
                data-testid="product-form-image-input"
                value={form.image_url}
                onChange={(e) => {
                  setImgOk(true);
                  set("image_url")(e);
                }}
                className="rounded-xl"
                placeholder="https://..."
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pf-original">Product URL</Label>
              <Input
                id="pf-original"
                data-testid="product-form-original-input"
                value={form.original_url}
                onChange={set("original_url")}
                className="rounded-xl"
                placeholder="https://www.amazon.in/dp/..."
              />
              <p className="text-xs text-zinc-400">The Buy Now button sends visitors to this link.</p>
            </div>

            <div className="grid grid-cols-2 items-end gap-3">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category_id} onValueChange={set("category_id")}>
                  <SelectTrigger data-testid="product-form-category-select" className="rounded-xl">
                    <SelectValue placeholder="Uncategorized" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Uncategorized</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-black/10 px-4 py-2.5">
                <Label htmlFor="pf-published" className="text-sm">
                  Published
                </Label>
                <Switch id="pf-published" data-testid="product-form-published-switch" checked={form.is_published} onCheckedChange={set("is_published")} />
              </div>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="product-form-save-button"
              className="w-full rounded-full bg-black py-5 text-white hover:bg-zinc-800"
            >
              {saving ? "Saving..." : isEdit ? "Save changes" : "Confirm & add product"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
